import { useState } from 'react'

import { useMonitors } from '@/hooks/useMonitors'
import { useProjects } from '@/hooks/useProjects'
import { useCreateMonitor, usePatchMonitor, useDeleteMonitor } from '@/hooks/useMutations'
import { Loading } from '@/components/ui/Loading'
import { ErrorState } from '@/components/ui/ErrorState'
import { EmptyState } from '@/components/ui/EmptyState'
import { Modal } from '@/components/ui/Modal'
import { t } from '@/lib/i18n'
import { formatRelativeTime } from '@/lib/format'
import type { Monitor, MonitorType } from '@/types/api'
import styles from './MonitorsPage.module.css'

const TYPE_BADGE: Record<MonitorType, { label: string; cls: string; icon: string }> = {
  http: { label: 'HTTP', cls: styles.typeHttp, icon: '🌐' },
  keyword: { label: 'HTTP', cls: styles.typeHttp, icon: '🌐' },
  ping: { label: 'PING', cls: styles.typeHttp, icon: '📡' },
  port: { label: 'PORT', cls: styles.typeHttp, icon: '🔌' },
  heartbeat: { label: 'HEARTBEAT', cls: styles.typeHeartbeat, icon: '⏰' },
  ssl: { label: 'SSL', cls: styles.typeSsl, icon: '🔒' },
  domain: { label: 'DOMAIN', cls: styles.typeDomain, icon: '🌐' },
}

function MonitorRow({
  monitor,
  onToggle,
  onDelete,
}: {
  monitor: Monitor
  onToggle: () => void
  onDelete: () => void
}) {
  const badge = TYPE_BADGE[monitor.type]!
  const dotClass =
    monitor.lastStatus === 'up'
      ? styles.dotUp
      : monitor.lastStatus === 'down'
        ? styles.dotDown
        : styles.dotPending

  const responseMs = monitor.lastResponseMs
  const responseClass = responseMs === null
    ? styles.responseNone
    : responseMs < 500
      ? styles.responseOk
      : styles.responseSlow

  const actionBtnStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    padding: '2px 6px',
    color: 'var(--color-text-tertiary)',
  }

  return (
    <div className={styles.row}>
      <span className={`${styles.dot} ${dotClass}`} />
      <div className={styles.content}>
        <div className={styles.top}>
          <span className={styles.name}>{monitor.name}</span>
          <span className={`${styles.typeBadge} ${badge.cls}`}>
            {badge.icon} {badge.label}
          </span>
        </div>
        <div className={styles.meta}>
          {monitor.targetUrl && <span className={styles.target}>{monitor.targetUrl}</span>}
          <span>· {formatRelativeTime(monitor.lastCheckAt)}</span>
        </div>
      </div>
      <div className={styles.lastResponse}>
        <span className={`${styles.responseTime} ${responseClass}`}>
          {responseMs !== null ? `${responseMs}ms` : '—'}
        </span>
        <button
          type="button"
          onClick={onToggle}
          style={actionBtnStyle}
          title={monitor.enabled ? '비활성화' : '활성화'}
        >
          {monitor.enabled ? '⏸' : '▶'}
        </button>
        <button
          type="button"
          onClick={onDelete}
          style={{ ...actionBtnStyle, color: 'var(--color-danger-text)' }}
          title="삭제"
        >
          ✕
        </button>
      </div>
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: 40,
  padding: '0 12px',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 14,
  outline: 'none',
  background: 'var(--color-surface)',
  color: 'var(--color-text)',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  marginBottom: 4,
  marginTop: 16,
}

export default function MonitorsPage() {
  const { data, isLoading, error, refetch } = useMonitors()
  const { data: projects } = useProjects()
  const createMonitor = useCreateMonitor()
  const patchMonitor = usePatchMonitor()
  const deleteMonitor = useDeleteMonitor()

  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState({
    projectId: '',
    name: '',
    type: 'http' as MonitorType,
    targetUrl: '',
    targetDomain: '',
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    createMonitor.mutate(form, {
      onSuccess: () => {
        setModalOpen(false)
        setForm({ projectId: '', name: '', type: 'http', targetUrl: '', targetDomain: '' })
      },
    })
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-md)' }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>{t('Monitors')}</h2>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          style={{
            padding: '8px 16px',
            background: 'var(--color-primary)',
            color: '#fff',
            borderRadius: 'var(--radius-sm)',
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          + {t('Add monitor')}
        </button>
      </div>

      {isLoading && <Loading variant="rows" count={6} />}
      {error && <ErrorState error={error} onRetry={() => refetch()} />}
      {data && data.length === 0 && (
        <EmptyState
          icon={<span>📡</span>}
          title="모니터가 없습니다"
          description="첫 모니터를 추가해 서비스 상태를 추적하세요."
        />
      )}
      {data && data.length > 0 && (
        <div className={styles.list}>
          {data.map((m) => (
            <MonitorRow
              key={m.id}
              monitor={m}
              onToggle={() => patchMonitor.mutate({ id: m.id, body: { enabled: !m.enabled } })}
              onDelete={() => {
                if (confirm(`"${m.name}" 모니터를 삭제하시겠습니까?`)) {
                  deleteMonitor.mutate(m.id)
                }
              }}
            />
          ))}
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="모니터 추가">
        <form onSubmit={handleSubmit}>
          <label style={labelStyle} htmlFor="m-project">프로젝트</label>
          <select
            id="m-project"
            style={inputStyle}
            value={form.projectId}
            onChange={(e) => setForm({ ...form, projectId: e.target.value })}
            required
          >
            <option value="">선택...</option>
            {projects?.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <label style={labelStyle} htmlFor="m-name">이름</label>
          <input
            id="m-name"
            style={inputStyle}
            type="text"
            placeholder="예: Vanko API Health"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />

          <label style={labelStyle} htmlFor="m-type">타입</label>
          <select
            id="m-type"
            style={inputStyle}
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value as MonitorType })}
          >
            <option value="http">HTTP (URL 체크)</option>
            <option value="heartbeat">Heartbeat (로컬 서비스)</option>
            <option value="ssl">SSL (인증서 만료)</option>
            <option value="domain">Domain (도메인 만료)</option>
          </select>

          {form.type === 'http' && (
            <>
              <label style={labelStyle} htmlFor="m-url">대상 URL</label>
              <input
                id="m-url"
                style={inputStyle}
                type="url"
                placeholder="https://example.com/api/health"
                value={form.targetUrl}
                onChange={(e) => setForm({ ...form, targetUrl: e.target.value })}
                required
              />
            </>
          )}

          {form.type === 'domain' && (
            <>
              <label style={labelStyle} htmlFor="m-domain">도메인</label>
              <input
                id="m-domain"
                style={inputStyle}
                type="text"
                placeholder="example.com"
                value={form.targetDomain}
                onChange={(e) => setForm({ ...form, targetDomain: e.target.value })}
                required
              />
            </>
          )}

          <button
            type="submit"
            disabled={createMonitor.isPending}
            style={{
              marginTop: 24,
              width: '100%',
              height: 44,
              background: 'var(--color-primary)',
              color: '#fff',
              borderRadius: 'var(--radius-sm)',
              fontWeight: 600,
              fontSize: 14,
              opacity: createMonitor.isPending ? 0.6 : 1,
            }}
          >
            {createMonitor.isPending ? '추가 중...' : '모니터 추가'}
          </button>
          {createMonitor.error && (
            <p style={{ marginTop: 8, fontSize: 13, color: 'var(--color-danger-text)' }}>
              {(createMonitor.error as Error).message}
            </p>
          )}
        </form>
      </Modal>
    </>
  )
}
